import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Info } from "lucide-react";

const PLANS = [
  {
    id: "decouverte",
    name: "Découverte",
    tagline: "Jusqu'à 50 biens",
    price: 50_000,
    maxProperties: 50,
    img: "https://images.pexels.com/photos/8146330/pexels-photo-8146330.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: ["2 utilisateurs", "Fonctionnalités essentielles", "Support email", "14 jours gratuits"],
  },
  {
    id: "pro",
    name: "Professionnelle",
    tagline: "Jusqu'à 200 biens",
    price: 100_000,
    maxProperties: 200,
    img: "https://images.pexels.com/photos/7587828/pexels-photo-7587828.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: ["5 utilisateurs", "Toutes les fonctionnalités", "Support prioritaire", "Formation incluse"],
  },
  {
    id: "entreprise",
    name: "Entreprise",
    tagline: "Biens illimités",
    price: 200_000,
    maxProperties: 500,
    img: "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODMyNzE4MjV8MA&ixlib=rb-4.1.0&q=85",
    features: ["Utilisateurs illimités", "Fonctionnalités personnalisées", "Support dédié", "Formation sur site"],
  },
];

const formatFCFA = (n) => `${Math.round(n).toLocaleString("fr-FR").replace(/,/g, " ")} FCFA`;

// ROI calculation based on properties managed
const calculateROI = (properties, hoursPerProperty = 3, hourlyRate = 5000, unpaidRate = 0.15, avgRent = 150000) => {
  const monthlyTimeSaved = properties * hoursPerProperty * 0.7; // 70% time savings
  const timeSavingsValue = monthlyTimeSaved * hourlyRate;
  const unpaidRecovered = properties * avgRent * unpaidRate * 0.9; // 90% reduction in unpaid
  return {
    timeSaved: monthlyTimeSaved,
    timeSavingsValue,
    unpaidRecovered,
    totalValue: timeSavingsValue + unpaidRecovered,
  };
};

const Configurator = () => {
  const [planId, setPlanId] = useState("pro");
  const plan = PLANS.find((p) => p.id === planId);

  const [properties, setProperties] = useState(50);

  React.useEffect(() => {
    // Reset properties to middle of plan range when plan changes
    const midProperties = Math.round(plan.maxProperties / 2);
    setProperties(Math.min(properties, plan.maxProperties) || midProperties);
  }, [planId]); // eslint-disable-line

  const roi = useMemo(() => calculateROI(properties), [properties]);
  const monthlyROI = roi.totalValue - plan.price;
  const annualROI = monthlyROI * 12;

  const pieData = [
    { name: "Temps gagné", value: roi.timeSavingsValue },
    { name: "Impayés récupérés", value: roi.unpaidRecovered },
  ];

  return (
    <section
      id="tarifs"
      data-testid="configurator-section"
      className="relative bg-[#FAFAF8] py-24 md:py-40 border-t border-[#0C2340]/8"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
      {/* 
        <div className="flex items-center gap-3 mb-8">
          <span className="mikassa-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-[#636E72]">
            Tarifs & ROI
          </span>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <h2
            data-testid="configurator-title"
            className="lg:col-span-8 font-heading font-black text-[#0C2340] text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Calculez.<br />
            <span className="text-[#E8611A]">Votre ROI.</span> Instantanément.
          </h2>
          {/* <p className="lg:col-span-4 font-body text-[#2D3436] text-base md:text-lg leading-relaxed">
            Trois formules adaptées à votre activité. Simulez vos économies
            en fonction du nombre de biens que vous gérez.
          </p> */}
        </div>

        {/* Plan selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5 mb-12">
          {PLANS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPlanId(p.id)}
              data-testid={`plan-${p.id}`}
              className={`group text-left relative overflow-hidden aspect-[4/3] transition-all duration-500 ${
                planId === p.id ? "ring-2 ring-[#E8611A]" : "ring-1 ring-[#0C2340]/10"
              }`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110"
                style={{ backgroundImage: `url(${p.img})` }}
              />
              <div
                className={`absolute inset-0 transition-opacity duration-500 ${
                  planId === p.id
                    ? "bg-gradient-to-t from-[#0C2340]/95 via-[#0C2340]/60 to-[#0C2340]/10"
                    : "bg-gradient-to-t from-[#0C2340]/85 via-[#0C2340]/40 to-transparent"
                }`}
              />
              <div className="relative h-full flex flex-col justify-between p-5 md:p-6 text-white">
                <div className="flex items-center justify-between">
                  <span
                    className="w-2 h-2 rotate-45"
                    style={{ background: planId === p.id ? "#E8611A" : "#FAFAF8" }}
                  />
                  <span className="font-body text-[0.55rem] uppercase tracking-[0.24em] opacity-70">
                    {p.tagline}
                  </span>
                </div>
                <div>
                  <div className="font-heading font-black text-2xl md:text-3xl tracking-tighter leading-none mb-1">
                    {p.name}
                  </div>
                  <div className="font-body text-[0.65rem] uppercase tracking-[0.22em] opacity-80">
                    {formatFCFA(p.price)}/mois
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Simulator */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Sliders */}
          <div className="lg:col-span-7 space-y-10">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#636E72]">
                  Nombre de biens gérés · Formule {plan.name}
                </label>
                <span className="font-heading font-black text-[#0C2340] text-xl md:text-2xl tracking-tight" data-testid="properties-value">
                  {properties} biens
                </span>
              </div>
              <Slider
                value={[properties]}
                min={5}
                max={plan.maxProperties}
                step={5}
                onValueChange={([v]) => setProperties(v)}
                data-testid="properties-slider"
              />
              <div className="flex justify-between mt-2 font-body text-[0.65rem] uppercase tracking-[0.22em] text-[#636E72]">
                <span>5 biens</span>
                <span>{plan.maxProperties} biens</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#636E72]">
                  Heures économisées par mois
                </label>
                <span className="font-heading font-black text-[#0C2340] text-xl md:text-2xl tracking-tight" data-testid="hours-value">
                  {Math.round(roi.timeSaved)} heures
                </span>
              </div>
              <div className="h-2 bg-[#0C2340]/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#E8611A] transition-all duration-300"
                  style={{ width: `${Math.min(100, (roi.timeSaved / 200) * 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 font-body text-[0.65rem] uppercase tracking-[0.22em] text-[#636E72]">
                <span>Équivalent : {formatFCFA(roi.timeSavingsValue)}</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#636E72]">
                  Impayés récupérés (estimation)
                </label>
                <span className="font-heading font-black text-[#0C2340] text-xl md:text-2xl tracking-tight" data-testid="unpaid-value">
                  {formatFCFA(roi.unpaidRecovered)}
                </span>
              </div>
              <div className="h-2 bg-[#0C2340]/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#E8611A] transition-all duration-300"
                  style={{ width: `${Math.min(100, (roi.unpaidRecovered / 5000000) * 100)}%` }}
                />
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3 mt-8 pt-8 border-t border-[#0C2340]/10">
              {plan.features.map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <span className="mikassa-diamond" />
                  <span className="font-body text-[#2D3436] text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Results panel */}
          <div className="lg:col-span-5 bg-[#0A1C33] text-white p-8 md:p-10 relative" data-testid="simulator-results">
            <div className="font-body text-[0.65rem] uppercase tracking-[0.28em] text-white/60 mb-3">
              Valeur mensuelle générée
            </div>
            <div className="font-heading font-black text-white text-5xl md:text-6xl tracking-tighter leading-none mb-2" data-testid="monthly-value">
              {formatFCFA(roi.totalValue)}
            </div>
            <div className="font-body text-white/50 text-xs">
              Économies + récupération d'impayés
            </div>

            {/* Donut */}
            <div className="h-52 my-8 -mx-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    innerRadius={55}
                    outerRadius={85}
                    startAngle={90}
                    endAngle={-270}
                    dataKey="value"
                    stroke="none"
                  >
                    <Cell fill="#E8611A" />
                    <Cell fill="#FAFAF8" />
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#0C2340", border: "none", color: "#FAFAF8", fontSize: 12 }}
                    formatter={(v) => formatFCFA(v)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 gap-6 border-t border-white/12 pt-6">
              <div>
                <div className="font-body text-white/60 text-[0.6rem] uppercase tracking-[0.24em]">
                  Coût de l'abonnement
                </div>
                <div className="font-heading font-black text-white text-lg md:text-xl tracking-tight mt-1" data-testid="subscription-value">
                  {formatFCFA(plan.price)}/mois
                </div>
              </div>
              <div>
                <div className="font-body text-white/60 text-[0.6rem] uppercase tracking-[0.24em]">
                  ROI mensuel net
                </div>
                <div className="font-heading font-black text-[#E8611A] text-lg md:text-xl tracking-tight mt-1" data-testid="roi-value">
                  +{formatFCFA(monthlyROI)}
                </div>
              </div>
              <div>
                <div className="font-body text-white/60 text-[0.6rem] uppercase tracking-[0.24em]">
                  ROI annuel net
                </div>
                <div className="font-heading font-black text-white text-lg md:text-xl tracking-tight mt-1">
                  +{formatFCFA(annualROI)}
                </div>
              </div>
              <div>
                <div className="font-body text-white/60 text-[0.6rem] uppercase tracking-[0.24em]">
                  Retour sur investissement
                </div>
                <div className="font-heading font-black text-[#E8611A] text-lg md:text-xl tracking-tight mt-1">
                  {Math.round((roi.totalValue / plan.price) * 100)}%
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-start gap-3 text-white/50">
              <Info size={14} className="mt-0.5 shrink-0" />
              <p className="font-body text-[0.65rem] leading-relaxed">
                Estimation basée sur 3h/bien/mois, taux horaire 5000 FCFA, loyer moyen 150 000 FCFA, 15% d'impayés avant Mikassa.
              </p>
            </div>

            <a
              href="#contact"
              className="mt-8 mikassa-btn-primary w-full justify-center"
              data-testid="simulator-cta"
            >
              Commencer l'essai gratuit
              <span className="mikassa-diamond" style={{ background: "#FAFAF8" }} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Configurator;
